import type { TFunction } from 'i18next';

import type { EnrichedQuality } from '../hooks/use-run-content';
import type { EnrichedProductField, RunEnrichedProduct } from '../types/run-content.api.types';

import { formatScore } from './run-format';

/**
 * What happened to one requested field on one product. Only `failed`, `notGenerated` and
 * `rejected` are problems (G1: colour only for those); the rest render neutral.
 */
export type EnrichedFieldKind =
  | 'generated'
  | 'edited'
  | 'unchanged'
  | 'notGenerated'
  | 'rejected'
  | 'failed';

/** Kinds that need a look — sorted first in the field sheet and drawn in warning/error tone. */
export const PROBLEM_KINDS: ReadonlySet<EnrichedFieldKind> = new Set([
  'failed',
  'notGenerated',
  'rejected',
]);

/** Field-state chip tone — colour only for problems (G1); the one implementation shared by every
 *  field-state chip (the compare table, the enrichment field diff). */
export function fieldKindBadgeClass(kind: EnrichedFieldKind): string {
  if (kind === 'failed') return 'bg-error-50 text-error border-error/30';
  if (PROBLEM_KINDS.has(kind)) return 'bg-warning-50 text-warning border-warning/30';
  return 'bg-muted text-muted-foreground border-border';
}

/** Field codes arrive as `google_product_category` or `GOOGLE_PRODUCT_CATEGORY` — one key for both. */
export function fieldKey(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

/** True when a field value carries nothing (null, blank string, empty list / object). */
function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/** True for text that carries nothing but separators (",,,,", " ; ; ") — the feed's empty list. */
function isSeparatorsOnly(text: string): boolean {
  return /^[\s,;|/]+$/.test(text);
}

/**
 * Formatters a caller plugs in to localize/label the generic value shape below. Left out, scalars
 * render plain (no code-label lookups) and true/false/empty fall back to English — fine for the
 * pure, non-rendered uses (word-diffing, change detection); render sites pass their own `t`.
 */
export interface ValueTextFormatters {
  /** A non-blank, non-separator-only string scalar, after trim; `key` is the value's own field. */
  string?: (value: string, key: string | null) => string;
  /** true/false as shown text. */
  boolean?: (value: boolean) => string;
  /** Text shown for a separators-only string or a list that collapses to nothing. */
  empty?: string;
  /** An object key as a human label, for "Key: value" entries. */
  label?: (key: string) => string;
}

/**
 * Any field/snapshot value as readable text, never JSON: scalars as-is (code-like values can be
 * labelled via `fmt.string`), booleans Yes/No, a money-like `{amount, currency}` as "24.00 GBP",
 * other objects as "Key: value · Key: value", lists joined with ", " with blank / separator-only
 * entries dropped (a 5-slot custom-label field with unset slots renders as "Empty", not blank
 * bullets). Null when there is nothing to show. The shared shape behind this file's `fieldValueText`
 * and utils/curation-field-values.ts `formatFieldValue` (which layers curation code-label lookups
 * on top) — one implementation so neither can fall out of step with the other.
 */
export function formatValueText(
  value: unknown,
  key: string | null,
  fmt: ValueTextFormatters = {},
): string | null {
  const str = fmt.string ?? ((v: string) => v);
  const bool = fmt.boolean ?? ((v: boolean) => (v ? 'Yes' : 'No'));
  const empty = fmt.empty ?? 'Empty';
  const label = fmt.label ?? ((k: string) => k);
  if (isEmptyValue(value)) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return isSeparatorsOnly(trimmed) ? empty : str(trimmed, key);
  }
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return bool(value);
  if (Array.isArray(value)) {
    const parts = value
      .map((v) => formatValueText(v, key, fmt))
      .filter((v): v is string => v !== null && v !== empty);
    return parts.length > 0 ? parts.join(', ') : empty;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const amount = record.amount ?? record.value;
    const currency = record.currency ?? record.currency_code ?? record.currencyCode;
    if (
      (typeof amount === 'string' || typeof amount === 'number') &&
      typeof currency === 'string'
    ) {
      return `${String(amount)} ${currency}`;
    }
    const parts = Object.entries(record)
      .map(([k, v]) => {
        const text = formatValueText(v, k, fmt);
        return text === null ? null : `${label(k)}: ${text}`;
      })
      .filter((p): p is string => p !== null);
    return parts.length > 0 ? parts.join(' · ') : null;
  }
  return null;
}

/**
 * A field value as display text (G11/G3 fix): delegates to `formatValueText` with no code-label
 * lookups. Pass `t` (catalog-v2-run) at render sites so Yes/No/Empty are localized; pure
 * comparison/diffing callers (word-diff, change detection) can omit it.
 */
export function fieldValueText(value: unknown, t?: TFunction<'catalog-v2-run'>): string | null {
  return formatValueText(value, null, {
    boolean: (v) => (t ? t(v ? 'common.yes' : 'common.no') : v ? 'Yes' : 'No'),
    empty: t ? t('enrichment.sheet.empty') : 'Empty',
  });
}

/** True when the agent produced a value for the field (the "generated" count). */
export function isGenerated(field: EnrichedProductField): boolean {
  return !isEmptyValue(field.generated);
}

/**
 * One field's kind. The server's `state` wins for failures and operator decisions
 * (GENERATED | APPROVED | REJECTED, or a FAILED/ERROR code); otherwise it is read off the
 * values: nothing generated → notGenerated, same as the feed → unchanged, else generated.
 */
export function enrichedFieldKind(field: EnrichedProductField): EnrichedFieldKind {
  const state = (field.state ?? '').toUpperCase();
  if (state.includes('FAIL') || state.includes('ERROR')) return 'failed';
  if (state === 'REJECTED') return 'rejected';
  if (state === 'APPROVED' || state === 'EDITED') return 'edited';
  if (!isGenerated(field)) return 'notGenerated';
  if (fieldValueText(field.generated) === fieldValueText(field.before)) return 'unchanged';
  return 'generated';
}

/** Kinds whose value differs from the feed's — what "Changed only" keeps. */
export function isChangedKind(kind: EnrichedFieldKind): boolean {
  return kind === 'generated' || kind === 'edited';
}

/** One product's field roll-up for the compact Fields cell. */
export interface FieldsSummary {
  generated: number;
  /** Fields asked for on this product: the run's requested set ∪ the fields the row lists. */
  requested: number;
  /** Codes of the fields generated, in row order — the cell shows the first few as chips. */
  generatedNames: string[];
  /** Any field that needs a look (failed / not generated / rejected). */
  problems: number;
}

/**
 * "5 of 5 generated" for one row. `requested` is the run's requested field keys (from field
 * sets) when known; the row's own field list is unioned in so a connector's always-on fields
 * still count, and with no field-set read the row's own list is the denominator.
 */
export function summarizeFields(
  row: RunEnrichedProduct,
  requested: ReadonlySet<string> | null,
): FieldsSummary {
  const keys = new Set(requested ?? []);
  const generatedNames: string[] = [];
  let problems = 0;
  for (const field of row.fields) {
    keys.add(fieldKey(field.name));
    if (isGenerated(field)) generatedNames.push(field.name);
    if (PROBLEM_KINDS.has(enrichedFieldKind(field))) problems += 1;
  }
  return {
    generated: generatedNames.length,
    requested: Math.max(keys.size, generatedNames.length),
    generatedNames,
    problems,
  };
}

/** True when the quality read has any score for the product. */
export function hasQuality(quality: EnrichedQuality | null | undefined): boolean {
  return !!quality && (quality.pre !== null || quality.post !== null);
}

/**
 * True when a field carries nothing on any side — the feed never had it, content-gen never
 * generated it, and no one sent a value for it — regardless of its reported `state` (a blanket
 * gate decision can mark every field "edited" even when some were never touched). Finding #13 —
 * behind the shared `NotGeneratedFieldsNote` collapse (journey page + delivery compare page).
 */
export function hasNoValue(field: EnrichedProductField): boolean {
  return (
    fieldValueText(field.before) === null &&
    fieldValueText(field.generated) === null &&
    fieldValueText(field.effective) === null
  );
}

/**
 * content-gen's own 0-10 self-grade, as "Agent self-grade 7.0 → 9.0 of 10" — only when it
 * actually sent one (null for most runs), so it never becomes an always-empty column.
 */
export function selfGradeText(
  row: Pick<RunEnrichedProduct, 'originalScore' | 'optimizedScore'>,
  t: TFunction<'catalog-v2-run'>,
): string | null {
  if (row.originalScore === null && row.optimizedScore === null) return null;
  return t('enrichment.quality.selfGrade', {
    before: formatScore(row.originalScore, 1),
    after: formatScore(row.optimizedScore, 1),
  });
}
